/**
 * 외부 이메일 가져오기 — 직원이 자기 메일함 백업 ZIP 을 올려 시스템으로 흡수
 *
 * 구조
 *   상단: ZIP 파일 1개 선택
 *   중단: 진행률 (서버 잡을 폴링) — 신규/통합/실패 카운터 + 취소
 *   하단: 내 업로드 이력 테이블 (최신순 페이지네이션, 행별 단건 취소)
 *
 * 예전 구조와 무엇이 달라졌나
 *   1) <b>ZIP 한 덩어리만 받는다.</b> 예전엔 .eml 을 여러 개(또는 폴더 통째로) 골라 건별로 POST 했다.
 *      브라우저가 파일 수천 개를 들고 있어야 했고, 중간에 화면을 벗어나면 남은 파일이 그대로 유실됐다.
 *   2) <b>받은/보낸/스팸/임시 탭이 사라졌다.</b> 그 정보는 백업 폴더 구조에 이미 들어 있다.
 *      사용자가 고르게 하면 폴더별로 4번 나눠 올려야 하고, 사용자가 만든 분류 폴더는 아예 표현할 수
 *      없었다. 이제 NAS 이관과 <b>같은 규칙</b>으로 경로를 읽어 자동 배정한다.
 *   3) <b>진행이 서버에 있다.</b> 업로드만 끝나면 화면을 닫아도 이관은 계속된다. 다시 들어오면
 *      진행 중인 작업을 그대로 이어서 보여준다(마운트 시 status 조회).
 *   4) "최근 100건 일괄 취소" 제거. 이관 단위가 ZIP 1개라 '취소' 는 진행 중 중단이면 충분하고,
 *      이미 들어간 건은 이력에서 골라 되돌리는 편이 안전하다.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box, Chip, IconButton, LinearProgress, Paper, Stack,
    TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import UploadFileRoundedIcon       from "@mui/icons-material/UploadFileRounded";
import ActionButton from "../../component/common/ActionButton";
import ResizableTable from "../../component/common/ResizableTable";
import DeleteRoundedIcon           from "@mui/icons-material/DeleteRounded";
import RefreshRoundedIcon          from "@mui/icons-material/RefreshRounded";
import CheckCircleRoundedIcon      from "@mui/icons-material/CheckCircleRounded";
import ContentCopyRoundedIcon      from "@mui/icons-material/ContentCopyRounded";
import ErrorRoundedIcon            from "@mui/icons-material/ErrorRounded";
import dayjs from "dayjs";
import { formatFileSize as formatSize } from "../../util/numberFormat";

import { migrationApi } from "./api/migrationApi";
import { ds }            from "../../styles/designSystem";
import { notify }        from "../../util/toast";
import { confirm }       from "../../util/confirmDialog";

const STATUS_LABEL = {
    CREATED:    { label: "신규",   color: "#16a34a", bg: "#dcfce7", icon: <CheckCircleRoundedIcon sx={{ fontSize: 14 }} /> },
    DUPLICATED: { label: "통합",   color: "#2563eb", bg: "#dbeafe", icon: <ContentCopyRoundedIcon sx={{ fontSize: 14 }} /> },
    FAILED:     { label: "실패",   color: "#dc2626", bg: "#fee2e2", icon: <ErrorRoundedIcon sx={{ fontSize: 14 }} /> },
};

const FOLDER_LABEL = {
    INBOX: "받은편지함", SENT: "보낸편지함", SPAM: "스팸", DRAFT: "임시저장", TRASH: "휴지통", ARCHIVE: "보관함",
};

/** 진행 폴링 주기. 이관은 초당 수 건이라 이보다 촘촘히 물어도 보이는 게 달라지지 않는다. */
const POLL_MS = 1500;

/** 초 → "2시간 13분" · "13분 5초" · "42초" (백엔드 로그 표기와 같은 규칙). */
function fmtDuration(sec) {
    if (sec == null) return "계산 중";
    if (sec < 60) return `${sec}초`;
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    return h > 0 ? `${h}시간 ${m}분` : `${m}분 ${s}초`;
}

export default function MailMigrationPage() {
    /* ── 서버 잡 상태 (진행률의 유일한 출처) ───────────────── */
    const [job, setJob]           = useState(null);
    const [uploading, setUploading] = useState(false);   // 파일 전송 중 (잡 시작 전)
    const pollRef = useRef(null);

    /* ── 업로드 이력 ─────────────────────────────────────── */
    const [history, setHistory]       = useState([]);
    const [page, setPage]             = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [historyLoading, setHistoryLoading] = useState(false);

    const running = !!job?.running;

    const loadHistory = useCallback(async (overridePage = null) => {
        setHistoryLoading(true);
        try {
            const res = await migrationApi.listMyUploads(overridePage ?? page, 30);
            const data = res?.data ?? res ?? {};
            setHistory(data.content || []);
            setTotalPages(Math.max(1, data.totalPages || 1));
        } catch (e) {
            // 예전엔 조용히 [] 로 두어, 서버 500 과 "업로드 이력 없음" 이 화면에서 구분되지 않았다.
            setHistory([]);
            notify(e?.message || "업로드 이력을 불러오지 못했습니다", "error");
        } finally {
            setHistoryLoading(false);
        }
    }, [page]);

    /* ── 진행 폴링 ───────────────────────────────────────── */
    const stopPolling = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };

    const startPolling = useCallback(() => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const res = await migrationApi.zipStatus();
                const s = res?.data ?? res ?? {};
                setJob(s.jobId ? s : null);
                if (!s.running) {
                    stopPolling();
                    loadHistory(0);          // 끝났으면 이력을 새로 받아 결과를 바로 보여준다
                    setPage(0);
                }
            } catch {
                // 일시적 네트워크 오류로 진행 표시를 지우지 않는다 — 다음 주기에 다시 물어본다.
            }
        }, POLL_MS);
    }, [loadHistory]);

    /* 마운트 시 한 번 — 화면을 벗어났다 돌아와도 진행 중인 작업을 이어서 보여준다.
       이관은 서버에서 도는 것이라 화면이 없어도 계속 진행된다. */
    useEffect(() => {
        loadHistory();
        (async () => {
            try {
                const res = await migrationApi.zipStatus();
                const s = res?.data ?? res ?? {};
                if (s.jobId) {
                    setJob(s);
                    if (s.running) startPolling();
                }
            } catch { /* 이력이 없으면 그냥 빈 화면 */ }
        })();
        return stopPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── ZIP 선택 → 업로드 ───────────────────────────────── */
    const handleFileSelected = async (fileList) => {
        const file = (fileList || [])[0];
        if (!file) return;
        if (!/\.zip$/i.test(file.name)) {
            notify("ZIP 파일만 올릴 수 있습니다", "warning");
            return;
        }
        setUploading(true);
        try {
            const res = await migrationApi.uploadZip(file);
            const s = res?.data ?? res ?? {};
            if (s.jobId) {
                setJob(s);
                startPolling();
                notify("업로드 완료 — 이관을 시작했습니다. 이 화면을 닫아도 계속 진행됩니다.", "success");
            } else {
                notify(s.message || "이관을 시작하지 못했습니다", "error");
            }
        } catch (e) {
            notify(e?.response?.data?.message || e?.message || "업로드에 실패했습니다", "error");
        } finally {
            setUploading(false);
        }
    };

    const handleCancel = async () => {
        const ok = await confirm({
            title: "이관 중단",
            message: "지금까지 들어간 메일은 그대로 남습니다. 되돌리려면 아래 이력에서 개별 취소하세요.\n중단할까요?",
            confirmText: "중단",
            danger: true,
        });
        if (!ok) return;
        try {
            await migrationApi.cancelZip(job?.jobId);
            notify("중단을 요청했습니다. 처리 중인 건까지 끝내고 멈춥니다.", "info");
        } catch (e) {
            notify(e?.response?.data?.message || "중단 요청에 실패했습니다", "error");
        }
    };

    /* ── 단건 취소 ──────────────────────────────────────── */
    const handleRevert = async (upload) => {
        const ok = await confirm({
            title: "업로드 취소",
            message: `'${upload.filename || "(파일명 없음)"}' 업로드를 취소합니다.\n이 메일이 다른 사용자에게도 배정돼 있으면 내 편지함에서만 사라집니다.`,
            confirmText: "취소하기",
            danger: true,
        });
        if (!ok) return;
        try {
            await migrationApi.revertUpload(upload.id);
            notify("업로드를 취소했습니다", "success");
            loadHistory();
        } catch (e) {
            notify(e?.response?.data?.message || "취소에 실패했습니다", "error");
        }
    };

    return (
        // 스크롤은 이 페이지가 담당한다 — 표는 자연 높이로 늘어나고 내부 스크롤을 만들지 않는다(시스템 공통 규칙).
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column",
                   overflow: "auto", ...ds.hiddenScrollbar }}>
            {/* ── 헤더 ─ */}
            <Stack direction="row" alignItems="center" mb={1}>
                <UploadFileRoundedIcon sx={{ fontSize: 22, color: ds.colors.primary, mr: 1 }} />
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                    외부 이메일 가져오기
                </Typography>
            </Stack>
            <Typography sx={{ fontSize: 12.5, color: "#64748b", mb: 2.5 }}>
                메일함 백업 ZIP 을 올리면 받은/보낸/스팸/임시와 직접 만든 분류 폴더까지 <b>구조 그대로</b> 복원합니다.
                같은 메일을 여러 사용자가 올려도 한 메일로 통합되며, 실수로 올렸으면 하단 이력에서 취소할 수 있습니다.
            </Typography>

            {/* ── ZIP 선택 ─ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#e2e8f0", bgcolor: "#fafbfc" }}>
                <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1.5 }}>
                    ZIP 안이 <code>계정/받은 메일함/파일.eml</code> 형태여야 폴더가 자동 배정됩니다.
                    압축을 풀지 말고 받은 그대로 올려주세요.
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <ActionButton
                        component="label"
                        variant="upload"
                        startIcon={<UploadFileRoundedIcon sx={{ fontSize: 16 }} />}
                        disabled={uploading || running}
                    >
                        {uploading ? "업로드 중..." : "ZIP 파일 선택"}
                        <input
                            type="file"
                            hidden
                            accept=".zip,application/zip"
                            // value 를 비우지 않으면 같은 파일을 다시 고를 때 change 가 안 떠서
                            // 두 번째 시도가 조용히 무시된다 (재시도 불가로 보임).
                            onChange={(e) => { handleFileSelected(e.target.files); e.target.value = ""; }}
                        />
                    </ActionButton>
                    {running && (
                        <Typography sx={{ fontSize: 12, color: "#92400e" }}>
                            이관이 진행 중입니다. 끝난 뒤에 다음 파일을 올려주세요.
                        </Typography>
                    )}
                </Stack>
            </Paper>

            {/* ── 진행률 ─ */}
            {job && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#bfdbfe", bgcolor: "#eff6ff" }}>
                    <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e40af" }}>
                            {running ? "이관 중..."
                                : job.state === "CANCELLED" ? "중단됨"
                                : job.state === "FAILED"    ? "실패"
                                : "완료"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#475569", flex: 1 }}>
                            {job.processed} / {job.total}
                            {running && ` · 남은시간 ${fmtDuration(job.etaSeconds)}`}
                        </Typography>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Chip label={`신규 ${job.created}`} size="small"
                                  sx={{ fontSize: 11, height: 20, bgcolor: "#dcfce7", color: "#166534" }} />
                            <Chip label={`통합 ${job.duplicated}`} size="small"
                                  sx={{ fontSize: 11, height: 20, bgcolor: "#dbeafe", color: "#1e40af" }} />
                            <Chip label={`실패 ${job.failed}`} size="small"
                                  sx={{ fontSize: 11, height: 20, bgcolor: "#fee2e2", color: "#991b1b" }} />
                            {running && (
                                <ActionButton variant="secondary" startIcon={false} onClick={handleCancel}
                                              label="중단" sx={{ fontSize: 12, color: "#dc2626",
                                                                borderColor: "rgba(220,38,38,0.35)" }} />
                            )}
                        </Stack>
                    </Stack>
                    <LinearProgress variant="determinate" value={job.percent || 0}
                                    sx={{ height: 6, borderRadius: 1, bgcolor: "#dbeafe" }} />
                    {running && job.current && (
                        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.7 }}>
                            처리 중: {job.current}
                        </Typography>
                    )}

                    {/* 정상적으로 빠진 것 — 실패가 아니라는 걸 분명히 한다 (macOS 부산물 등) */}
                    {job.skipped > 0 && (
                        <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.7 }}>
                            메일이 아닌 파일 {job.skipped}건은 건너뛰었습니다 (압축 과정에서 생긴 부산물 — 정상입니다).
                        </Typography>
                    )}
                    {/* 이건 사람이 봐야 한다 — ZIP 구조가 예상과 다르다는 신호 */}
                    {job.unreadable > 0 && (
                        <Box sx={{ mt: 1.5, p: 1, bgcolor: "#fff", border: "1px solid #fde68a", borderRadius: 1 }}>
                            <Typography sx={{ fontSize: 11, color: "#92400e" }}>
                                폴더 구조를 알아보지 못한 파일 {job.unreadable}건이 있습니다.
                                ZIP 안이 <code>계정/메일함/파일.eml</code> 형태인지 확인해주세요.
                            </Typography>
                        </Box>
                    )}
                    {job.failureMessage && (
                        <Typography sx={{ fontSize: 11.5, color: "#991b1b", mt: 1 }}>
                            {job.failureMessage}
                        </Typography>
                    )}
                    {job.errors?.length > 0 && (
                        <Box sx={{ mt: 1.5, p: 1, bgcolor: "#fff", border: "1px solid #fecaca", borderRadius: 1 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#991b1b", mb: 0.5 }}>
                                실패 ({job.failed}건)
                            </Typography>
                            <Box sx={{ maxHeight: 120, overflow: "auto" }}>
                                {job.errors.map((er, i) => (
                                    <Typography key={i} sx={{ fontSize: 11, color: "#7f1d1d", fontFamily: "monospace" }}>
                                        {er}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Paper>
            )}

            {/* ── 업로드 이력 ─ */}
            <Stack direction="row" alignItems="center" mb={1.5}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a", flex: 1 }}>
                    내 업로드 이력
                </Typography>
                <Tooltip title="새로고침">
                    <span>{/* disabled IconButton 의 Tooltip 은 wrapper 필요 */}
                        <IconButton size="small" onClick={() => loadHistory()}>
                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            <Paper variant="outlined" sx={{ borderColor: "#e2e8f0", overflowX: "clip", overflowY: "visible", flexShrink: 0 }}>
                <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell width={70}>상태</TableCell>
                            <TableCell>파일명 / 메일 제목</TableCell>
                            <TableCell width={110}>원본 메일함</TableCell>
                            <TableCell width={90}>폴더</TableCell>
                            <TableCell width={70} align="right">크기</TableCell>
                            <TableCell width={130}>일시</TableCell>
                            <TableCell width={50} align="right"></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {historyLoading ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#94a3b8" }}>불러오는 중...</TableCell></TableRow>
                        ) : history.length === 0 ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 12.5 }}>
                                업로드 이력이 없습니다. 위에서 메일함 백업 ZIP 을 선택하세요.
                            </TableCell></TableRow>
                        ) : history.map((h) => {
                            const sl = STATUS_LABEL[h.status] || STATUS_LABEL.FAILED;
                            return (
                                <TableRow key={h.id} hover>
                                    <TableCell>
                                        <Chip
                                            icon={sl.icon}
                                            label={sl.label}
                                            size="small"
                                            sx={{ fontSize: 10.5, height: 22, bgcolor: sl.bg, color: sl.color, fontWeight: 700,
                                                  "& .MuiChip-icon": { color: sl.color, ml: "4px" } }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>
                                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", wordBreak: "break-all" }}>
                                            {h.filename || "(파일명 없음)"}
                                        </Typography>
                                        {h.mailSubject && (
                                            <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.2 }}>
                                                📧 {h.mailSubject} {h.mailFromAddr && <span style={{ color: "#94a3b8" }}>· {h.mailFromAddr}</span>}
                                            </Typography>
                                        )}
                                        {h.failureReason && (
                                            <Typography sx={{ fontSize: 10.5, color: "#dc2626", mt: 0.2, fontFamily: "monospace" }}>
                                                {h.failureReason}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, color: "#64748b" }}>{h.sourceFolder || "-"}</TableCell>
                                    <TableCell sx={{ fontSize: 11, color: "#475569" }}>
                                        {h.targetFolder ? (FOLDER_LABEL[h.targetFolder] || h.targetFolder) : "-"}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, color: "#94a3b8" }} align="right">{formatSize(h.emlSizeBytes)}</TableCell>
                                    <TableCell sx={{ fontSize: 11, color: "#94a3b8" }}>
                                        {h.uploadedAt ? dayjs(h.uploadedAt).format("YY-MM-DD HH:mm") : "-"}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="이 업로드 취소 (실수 복구)">
                                            <span>
                                                <IconButton size="small" onClick={() => handleRevert(h)}>
                                                    <DeleteRoundedIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>
            </Paper>

            {/* 페이지네이션 */}
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} mt={1.5}>
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>{page + 1} / {totalPages}</Typography>
                <ActionButton variant="secondary" startIcon={false} disabled={page === 0}
                    onClick={() => { const np = page - 1; setPage(np); loadHistory(np); }}
                    label="이전" />
                <ActionButton variant="secondary" startIcon={false} disabled={page + 1 >= totalPages}
                    onClick={() => { const np = page + 1; setPage(np); loadHistory(np); }}
                    label="다음" />
            </Stack>
        </Box>
    );
}
